import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Checkbox } from "../ui/checkbox";
import { toast } from "sonner";
import { X, Plus } from "lucide-react";
import { api, errDetail } from "../../lib/api";
import { CommodityGroupPicker } from "./CommodityGroupPicker";
import { SUPPLIER_TYPES, CERT_SUGGESTIONS } from "../../lib/commodity";

const TIERS = ["strategic", "preferred", "approved", "tail"];
const INCOTERMS = ["EXW", "FCA", "FOB", "CFR", "CIF", "CPT", "CIP", "DAP", "DPU", "DDP"];
const CURRENCIES = ["EUR", "USD", "GBP", "CHF", "PLN", "CZK"];

function Sec({ title, children }) {
  return (
    <div className="space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</h4>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}
function F({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <Label className="text-xs">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

// Full best-in-class supplier master-data editor. `data` = /suppliers/{id} response,
// `plants` = list from /plants. Saves company + profile, then calls onSaved().
export function SupplierEditDialog({ data, plants = [], canComm, onClose, onSaved }) {
  const [c, setC] = useState({
    name: data.name || "", legal_form: data.legal_form || "", vat_id: data.vat_id || "",
    duns_number: data.duns_number || "", eori: data.eori || "", tax_number: data.tax_number || "",
    street: data.street || "", postal_code: data.postal_code || "", city: data.city || "",
    country: data.country || "", website: data.website || "", phone: data.phone || "",
    email: data.email || "", industry: data.industry || "",
  });
  const p0 = data.profile || {};
  const [p, setP] = useState({
    tier: p0.tier || "tail",
    commodity_group_ids: p0.commodity_group_ids || [],
    supplier_types: p0.supplier_types || [],
    supplied_plant_ids: p0.supplied_plant_ids || [],
    certifications: p0.certifications || [],
    default_incoterm: p0.default_incoterm || "",
    default_currency: p0.default_currency || "EUR",
    payment_terms_days: p0.payment_terms_days ?? "",
    payment_terms_text: p0.payment_terms_text || "",
    min_order_value: p0.min_order_value ?? "",
    min_order_currency: p0.min_order_currency || "EUR",
    lead_time_days: p0.lead_time_days ?? "",
    founded_year: p0.founded_year ?? "",
  });
  const [saving, setSaving] = useState(false);
  const [newCert, setNewCert] = useState("");

  const setCf = (k, v) => setC((o) => ({ ...o, [k]: v }));
  const setPf = (k, v) => setP((o) => ({ ...o, [k]: v }));
  const toggleType = (id) =>
    setPf("supplier_types", p.supplier_types.includes(id)
      ? p.supplier_types.filter((x) => x !== id) : [...p.supplier_types, id]);
  const togglePlant = (id) =>
    setPf("supplied_plant_ids", p.supplied_plant_ids.includes(id)
      ? p.supplied_plant_ids.filter((x) => x !== id) : [...p.supplied_plant_ids, id]);
  const addCert = (v) => {
    const val = (v || "").trim();
    if (val && !p.certifications.includes(val)) setPf("certifications", [...p.certifications, val]);
    setNewCert("");
  };

  const save = async () => {
    if (!c.name.trim()) { toast.error("Firmenname ist erforderlich"); return; }
    if (!window.confirm("Änderungen speichern?")) return;
    setSaving(true);
    try {
      await api.put(`/suppliers/${data.id}/company`, c);
      const num = (v) => (v === "" || v === null ? null : Number(v));
      await api.put(`/suppliers/${data.id}/profile`, {
        ...p,
        payment_terms_days: num(p.payment_terms_days),
        min_order_value: num(p.min_order_value),
        lead_time_days: num(p.lead_time_days),
        founded_year: num(p.founded_year),
      });
      toast.success("Stammdaten gespeichert ✓");
      onSaved();
    } catch (e) { toast.error(errDetail(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="supplier-edit-dialog">
        <DialogHeader>
          <DialogTitle>Lieferanten-Stammdaten bearbeiten</DialogTitle>
          <DialogDescription>{data.name} · {data.account_number}</DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <Sec title="Firma">
            <F label="Firmenname *" full><Input value={c.name} onChange={(e) => setCf("name", e.target.value)} data-testid="sup-edit-name" /></F>
            <F label="Rechtsform"><Input value={c.legal_form} placeholder="z. B. GmbH" onChange={(e) => setCf("legal_form", e.target.value)} data-testid="sup-edit-legal-form" /></F>
            <F label="Branche"><Input value={c.industry} onChange={(e) => setCf("industry", e.target.value)} data-testid="sup-edit-industry" /></F>
            <F label="USt-IdNr."><Input value={c.vat_id} onChange={(e) => setCf("vat_id", e.target.value)} data-testid="sup-edit-vat" /></F>
            <F label="Steuernummer"><Input value={c.tax_number} onChange={(e) => setCf("tax_number", e.target.value)} data-testid="sup-edit-tax" /></F>
            <F label="DUNS"><Input value={c.duns_number} onChange={(e) => setCf("duns_number", e.target.value)} data-testid="sup-edit-duns" /></F>
            <F label="EORI"><Input value={c.eori} onChange={(e) => setCf("eori", e.target.value)} data-testid="sup-edit-eori" /></F>
            <F label="Gründungsjahr"><Input type="number" value={p.founded_year} onChange={(e) => setPf("founded_year", e.target.value)} data-testid="sup-edit-founded" /></F>
          </Sec>

          <Sec title="Adresse & Kontakt">
            <F label="Straße" full><Input value={c.street} onChange={(e) => setCf("street", e.target.value)} data-testid="sup-edit-street" /></F>
            <F label="PLZ"><Input value={c.postal_code} onChange={(e) => setCf("postal_code", e.target.value)} data-testid="sup-edit-plz" /></F>
            <F label="Ort"><Input value={c.city} onChange={(e) => setCf("city", e.target.value)} data-testid="sup-edit-city" /></F>
            <F label="Land"><Input value={c.country} onChange={(e) => setCf("country", e.target.value)} data-testid="sup-edit-country" /></F>
            <F label="Website"><Input value={c.website} onChange={(e) => setCf("website", e.target.value)} data-testid="sup-edit-website" /></F>
            <F label="Telefon"><Input value={c.phone} onChange={(e) => setCf("phone", e.target.value)} data-testid="sup-edit-phone" /></F>
            <F label="E-Mail"><Input value={c.email} onChange={(e) => setCf("email", e.target.value)} data-testid="sup-edit-email" /></F>
          </Sec>

          <Sec title="Klassifizierung">
            <F label="Tier">
              <select className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" value={p.tier}
                onChange={(e) => setPf("tier", e.target.value)} data-testid="sup-edit-tier">
                {TIERS.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </F>
            <F label="Lieferantenart" full>
              <div className="flex flex-wrap gap-2" data-testid="sup-edit-types">
                {SUPPLIER_TYPES.map((t) => (
                  <label key={t.id} className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs cursor-pointer ${p.supplier_types.includes(t.id) ? "bg-[#1B2A4A] text-white border-[#1B2A4A]" : "bg-white text-gray-700 border-gray-300"}`}
                    data-testid={`sup-type-${t.id}`}>
                    <Checkbox className="hidden" checked={p.supplier_types.includes(t.id)} onCheckedChange={() => toggleType(t.id)} />
                    <span onClick={() => toggleType(t.id)}>{t.label}</span>
                  </label>
                ))}
              </div>
            </F>
            <F label="Warengruppen" full><CommodityGroupPicker value={p.commodity_group_ids} onChange={(v) => setPf("commodity_group_ids", v)} /></F>
          </Sec>

          <Sec title="Belieferte Standorte (unsere Werke)">
            <div className="col-span-2 flex flex-wrap gap-2" data-testid="sup-edit-plants">
              {plants.length === 0 && <span className="text-sm text-gray-400">Keine Werke gepflegt.</span>}
              {plants.map((pl) => (
                <label key={pl.id} className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs cursor-pointer ${p.supplied_plant_ids.includes(pl.id) ? "bg-[#F5A623]/20 border-[#F5A623] text-[#1B2A4A]" : "bg-white text-gray-700 border-gray-300"}`}
                  data-testid={`sup-plant-${pl.id}`} onClick={() => togglePlant(pl.id)}>
                  {pl.name}
                </label>
              ))}
            </div>
          </Sec>

          <Sec title="Zertifizierungen & Leistung">
            <F label="Zertifizierungen" full>
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="sup-edit-certs">
                {p.certifications.map((cert) => (
                  <Badge key={cert} variant="outline" className="gap-1 text-xs">{cert}
                    <button type="button" onClick={() => setPf("certifications", p.certifications.filter((x) => x !== cert))} className="text-gray-400 hover:text-red-500"><X className="h-3 w-3" /></button>
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={newCert} list="cert-suggestions" placeholder="Zertifikat hinzufügen" className="h-8"
                  onChange={(e) => setNewCert(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCert(newCert); } }}
                  data-testid="sup-cert-input" />
                <datalist id="cert-suggestions">{CERT_SUGGESTIONS.map((s) => <option key={s} value={s} />)}</datalist>
                <Button type="button" variant="outline" size="sm" onClick={() => addCert(newCert)} data-testid="sup-cert-add"><Plus className="h-4 w-4" /></Button>
              </div>
            </F>
            <F label="Lieferzeit (Tage)"><Input type="number" value={p.lead_time_days} onChange={(e) => setPf("lead_time_days", e.target.value)} data-testid="sup-edit-leadtime" /></F>
            {canComm && <F label="Mindestbestellwert"><div className="flex gap-2">
              <Input type="number" value={p.min_order_value} onChange={(e) => setPf("min_order_value", e.target.value)} data-testid="sup-edit-moq" />
              <select className="h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" value={p.min_order_currency} onChange={(e) => setPf("min_order_currency", e.target.value)}>
                {CURRENCIES.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div></F>}
          </Sec>

          {canComm && (
            <Sec title="Kaufmännisch">
              <F label="Zahlungsziel (Tage)"><Input type="number" value={p.payment_terms_days} onChange={(e) => setPf("payment_terms_days", e.target.value)} data-testid="sup-edit-pt-days" /></F>
              <F label="Zahlungsziel (Text)"><Input value={p.payment_terms_text} placeholder="z. B. 30 Tage netto" onChange={(e) => setPf("payment_terms_text", e.target.value)} data-testid="sup-edit-pt-text" /></F>
              <F label="Incoterm">
                <select className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" value={p.default_incoterm} onChange={(e) => setPf("default_incoterm", e.target.value)} data-testid="sup-edit-incoterm">
                  <option value="">–</option>{INCOTERMS.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </F>
              <F label="Währung">
                <select className="w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm" value={p.default_currency} onChange={(e) => setPf("default_currency", e.target.value)} data-testid="sup-edit-currency">
                  {CURRENCIES.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </F>
            </Sec>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t mt-4">
          <Button variant="outline" onClick={onClose}>Abbrechen</Button>
          <Button className="bg-[#1B2A4A] text-white" onClick={save} disabled={saving} data-testid="supplier-edit-save-btn">
            {saving ? "Speichert…" : "Speichern"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
